import { GigyaResponseType, LoginUserType, RegisterUserType } from './types';
import { AppThunk } from '../types';
import { history } from '@xarvio/frontend-core-platform/lib/browserHistory';
import { decodeToken, getGigyaErrorMessage } from './utils';
import { authService } from './authService';
import authSlice from './authSlice';
import featureFlagsSlice from '../featureFlags/featureFlagsSlice';
import intlSlice from '../intl/intlSlice';
import { userSettingsSlice } from '../users/settings/userSettingsSlice';
import { selectedFarmSlice } from '../selectedFarm/selectedFarmSlice';
import { selectedFieldsSlice } from '../selectedFields/selectedFieldsSlice';
import {
  LocalStorageKey,
  LocalStorageUtil,
} from '@xarvio/frontend-core-platform/lib/storage';
import { SessionStorageUtil } from '@xarvio/frontend-core-platform/lib/utils/sessionStorage';
import cookieReader from 'js-cookie';
import { logout as logoutUtil } from './utils/logout';
import { getPagesUrl } from '@xarvio/frontend-core-platform/lib/routerUrls';
import {
  getReturnUrl,
  appendHandoffToken,
} from '@xarvio/frontend-core-platform/lib/returnUrl';

const login = (
  { email, password }: LoginUserType,
  redirectUserHome = true,
): AppThunk => {
  return async (dispatch): Promise<void> => {
    dispatch(authSlice.actions.requestAuthStart());

    let responseData: GigyaResponseType | undefined;

    try {
      responseData = await authService.login({ email, password });

      if (responseData?.sessionInfo?.cookieValue) {
        cookieReader.set(
          import.meta.env.VITE_GIGYA_LOGIN_TOKEN_NAME,
          responseData.sessionInfo.cookieValue,
          {
            domain: import.meta.env.VITE_TOKEN_DOMAIN,
            sameSite: 'Strict',
          },
        );
      }

      const error = getGigyaErrorMessage(responseData);

      if (error) {
        dispatch(authSlice.actions.requestAuthFailure(error));
        return;
      }
    } catch (error) {
      console.error(error);
      dispatch(
        authSlice.actions.requestAuthFailure({
          statusCode: error.code,
          message: error.message,
        }),
      );
      return;
    }

    const { UID, UIDSignature, signatureTimestamp } = responseData;

    try {
      const user = await authService.getToken({
        UID,
        UIDSignature,
        signatureTimestamp,
      });

      const { token } = user;

      LocalStorageUtil.remove(LocalStorageKey.CFD_FILTER_STATE);

      dispatch(authSlice.actions.requestSuccessLogin(token));
      dispatch(authSlice.actions.setLoginToken(btoa(`${email}:${password}`)));
      dispatch(featureFlagsSlice.actions.setTokenFeatures(token));

      if (redirectUserHome) {
        const { country, language } = decodeToken(token);
        const locale = `${language}_${country}`;

        dispatch(intlSlice.actions.changeLocale(locale));

        const returnUrl = getReturnUrl(
          new URLSearchParams(window.location.search),
        );

        if (returnUrl) {
          window.location.href = appendHandoffToken(returnUrl, token);
          return;
        }

        const { landingPageUrl } = getPagesUrl({
          locale,
          country,
        });

        history.push(landingPageUrl);
      }
    } catch (error) {
      dispatch(authSlice.actions.requestAuthFailure(error));
    }
  };
};

export const register =
  (user: RegisterUserType, recaptchaToken: string): AppThunk =>
  async (dispatch) => {
    try {
      await authService.register(user, recaptchaToken);
      dispatch(login({ email: user.email, password: user.password }, false));
      dispatch(authSlice.actions.requestAuthSuccess());
      dispatch(authSlice.actions.setIsRegistering(true));
      // DFITE-33459: prevent showing what's new modal to new users
      LocalStorageUtil.set(
        LocalStorageKey.WHATS_NEW_MODAL_DISPLAYED_ON,
        new Date().toISOString(),
      );
    } catch (error) {
      dispatch(authSlice.actions.requestAuthFailure(error));
    }
  };

interface Logout {
  locale: string;
  redirectPath?: string;
  useHardRedirect?: boolean;
}

export const logout =
  ({ locale, redirectPath, useHardRedirect }: Logout): AppThunk =>
  async (dispatch) => {
    const { clearApolloStoreBeforeLogout } = await import(
      './clearApolloStoreBeforeLogout'
    );

    await clearApolloStoreBeforeLogout();

    dispatch(authSlice.actions.logout());
    dispatch(featureFlagsSlice.actions.removeTokenFeatures());
    dispatch(userSettingsSlice.actions.resetSettings());
    dispatch(selectedFarmSlice.actions.resetSelectedFarm());
    dispatch(selectedFieldsSlice.actions.resetSelectedFields());
    SessionStorageUtil.delete(
      SessionStorageUtil.KEY_NAMES.GRAPES_ONBOARDING_DISMISSED_FARM_UUIDS,
    );

    const nextUrl = logoutUtil({ locale, redirectPath, useHardRedirect });
    if (useHardRedirect === false && nextUrl) {
      history.push(nextUrl);
    }
  };

type SetNewPassword = {
  email: string;
  password: string;
  passwordToken?: string;
  isMobile?: boolean;
};

const setNewPassword = ({
  email,
  password,
  passwordToken,
  isMobile,
}: SetNewPassword): AppThunk => {
  return async (dispatch): Promise<void> => {
    dispatch(authSlice.actions.requestAuthStart());

    if (!passwordToken || passwordToken.length === 0) {
      dispatch(
        authSlice.actions.requestAuthFailure({
          reason: 'TECH',
          message: 'component.ResetPassword.pwrtNotProvided',
        }),
      );
      return;
    }

    const responseData = await authService.resetPassword({
      email,
      password,
      passwordToken,
    });

    const error = getGigyaErrorMessage(responseData);

    if (error) {
      dispatch(authSlice.actions.requestAuthFailure(error));
      return;
    } else {
      dispatch(authSlice.actions.requestAuthSuccess());
    }

    if (!isMobile) {
      dispatch(login({ email, password }));
    }
  };
};

export const userActions = {
  login,
  logout,
  register,
  setNewPassword,
};
